// @ts-nocheck
/** @jsxImportSource @emotion/react */

import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { useState } from 'react'
import Icon from 'src/components/Icon'
import { useSelector } from 'src/hooks/useRedux'
import Text from 'src/components/ts-components/Text'
import { formatPhoneNumber } from 'react-phone-number-input'
import EditAccountModal from './EditAccountModal'

interface ActionButtonProps {
  iconName: string
  label: string
  onClick: () => void
  removeMargin?: boolean
}

export const ActionButton = ({
  iconName,
  label,
  onClick,
  removeMargin,
}: ActionButtonProps) => (
  <button
    onClick={onClick}
    css={css`
      margin-left: ${removeMargin ? 0 : '20px'};
    `}
  >
    <Icon name={iconName} color={colors.DEFAULT_FLAIR} fontSize={14} />
    <span
      css={css`
        color: ${colors.DEFAULT_FLAIR};
        margin-left: 8px;
        font-size: 15px;
      `}
    >
      {label}
    </span>
  </button>
)

const ReadOnlyField = ({
  label,
  value,
}: {
  label: string
  value?: string | null
}) => (
  <div
    css={css`
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
    `}
  >
    <Text variant='paragraphSmall' weight='regular' color={colors.NEUTRAL_50}>
      {label}
    </Text>
    <Text variant='paragraphMedium' weight='regular' color={colors.WHITE}>
      {value || '—'}
    </Text>
  </div>
)

const rowStyle = css`
  display: flex;
  gap: 16px;
`

export const Details = () => {
  const account = useSelector((state: any) => state.account._meAccount)
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <>
      <div
        css={css`
          background-color: ${colors.HEADER};
          border: 1px solid ${colors.SECONDARY_200};
          padding: 16px;
          border-radius: 8px;
          max-width: 1080px;
          margin: 16px auto 0;
          display: flex;
          flex-direction: column;
          gap: 16px;
        `}
      >
        {/* Header */}
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <Text variant='heading4' weight='semiBold' color={colors.NEUTRAL_0}>
              CONTACT INFO
            </Text>
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              Manage Your Contact Information.
            </Text>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              width: 32px;
              height: 32px;
              padding: 0;
              border: 1px solid ${colors.PRIMARY};
              border-radius: 8px;
              background: transparent;
              cursor: pointer;
              line-height: 0;
              transition: background-color 0.2s;

              &:hover {
                background-color: ${colors.SECONDARY_200};
              }
            `}
          >
            <Icon name='pen' color={colors.PRIMARY} fontSize={16} />
          </button>
        </div>

        {/* Divider */}
        <div
          css={css`
            height: 1px;
            background-color: ${colors.SECONDARY_200};
          `}
        />

        {/* Contact Info */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          <div css={rowStyle}>
            <ReadOnlyField label='Full Name' value={account?.full_name} />
            <ReadOnlyField label='Title' value={account?.title} />
          </div>
          <div css={rowStyle}>
            <ReadOnlyField
              label='Phone Number'
              value={
                account?.phone_number
                  ? formatPhoneNumber(account.phone_number)
                  : undefined
              }
            />
            <ReadOnlyField label='Email' value={account?.email} />
          </div>
          <div css={rowStyle}>
            <ReadOnlyField label='Company Name' value={account?.company_name} />
            <div
              css={css`
                flex: 1;
              `}
            />
          </div>
        </div>

        {/* Divider */}
        <div
          css={css`
            height: 1px;
            background-color: ${colors.SECONDARY_200};
          `}
        />

        {/* Address */}
        <Text variant='heading4' weight='semiBold' color={colors.NEUTRAL_0}>
          ADDRESS
        </Text>

        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          <div css={rowStyle}>
            <ReadOnlyField label='Street' value={account?.address?.street_1} />
            <div
              css={css`
                flex: 1;
              `}
            />
          </div>
          <div css={rowStyle}>
            <ReadOnlyField label='City' value={account?.address?.city} />
            <ReadOnlyField
              label='State/Province'
              value={account?.address?.province?.name}
            />
          </div>
          <div css={rowStyle}>
            <ReadOnlyField
              label='ZIP/Postal Code'
              value={account?.address?.postal_code}
            />
            <ReadOnlyField
              label='Country'
              value={account?.address?.country?.name}
            />
          </div>
        </div>
      </div>

      <EditAccountModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  )
}
